/**
 * Central Tracking Configuration
 * Agency-grade configuration for all tracking services
 * 
 * SETUP INSTRUCTIONS:
 * 1. Replace placeholder IDs with actual client IDs
 * 2. Set enabled: true for active trackers
 * 3. Configure conversion events per client needs
 */

export interface TrackingConfig {
  // Google Analytics 4
  ga4: {
    enabled: boolean;
    measurementId: string;
  };
  
  // Google Ads
  googleAds: {
    enabled: boolean;
    conversionId: string;
    conversionLabel?: string;
  };
  
  // Meta (Facebook/Instagram)
  meta: {
    enabled: boolean;
    pixelId: string;
    accessToken?: string; // For CAPI
  };
  
  // TikTok
  tiktok: {
    enabled: boolean;
    pixelId: string;
  };
  
  // LinkedIn
  linkedin: {
    enabled: boolean;
    partnerId: string;
  };
  
  // Microsoft/Bing Ads
  bing: {
    enabled: boolean;
    tagId: string;
  };
}

export interface ConsentConfig {
  // Cookie settings
  cookieName: string;
  cookieDomain?: string;
  cookieExpireDays: number;
  
  // Policy URLs (per language)
  policyUrls: {
    privacy: Record<string, string>;
    cookies: Record<string, string>;
  };
  
  // Consent version for audit
  policyVersion: string;
  
  // Regional settings
  defaultRegion: 'gdpr' | 'ccpa' | 'global';
  showDoNotSell: boolean;
}

// Default tracking configuration with placeholders
export const trackingConfig: TrackingConfig = {
  ga4: {
    enabled: true,
    measurementId: import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-XXXXXXXXXX',
  },
  googleAds: {
    enabled: true,
    conversionId: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID || 'AW-XXXXXXXXX',
    conversionLabel: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL || '',
  },
  meta: {
    enabled: true,
    pixelId: import.meta.env.VITE_META_PIXEL_ID || 'XXXXXXXXXXXXXXX',
    accessToken: undefined, // Set via Supabase secrets for CAPI
  },
  tiktok: {
    enabled: false,
    pixelId: import.meta.env.VITE_TIKTOK_PIXEL_ID || 'XXXXXXXXXX',
  },
  linkedin: {
    enabled: false,
    partnerId: import.meta.env.VITE_LINKEDIN_PARTNER_ID || 'XXXXXXX',
  },
  bing: {
    enabled: false,
    tagId: import.meta.env.VITE_BING_TAG_ID || 'XXXXXXXX',
  },
};

export const consentConfig: ConsentConfig = {
  cookieName: 'cookie_consent',
  cookieExpireDays: 365,
  policyVersion: '1.0.0',
  defaultRegion: 'gdpr',
  showDoNotSell: true,
  policyUrls: {
    privacy: {
      en: '/privacy-policy',
      es: '/es/politica-de-privacidad',
      vi: '/vi/chinh-sach-bao-mat',
    },
    cookies: {
      en: '/privacy-policy#cookies',
      es: '/es/politica-de-privacidad#cookies',
      vi: '/vi/chinh-sach-bao-mat#cookies',
    },
  },
};

// Standard event names for consistency across platforms
export const standardEvents = {
  PAGE_VIEW: 'page_view',
  VIEW_CONTENT: 'view_content',
  CLICK_CTA: 'click_cta',
  LEAD_SUBMIT: 'lead_submit',
  FORM_START: 'form_start',
  FORM_SUBMIT: 'form_submit',
  PHONE_CLICK: 'phone_click',
  EMAIL_CLICK: 'email_click',
  PURCHASE: 'purchase',
  BOOKING: 'booking',
  CONSULTATION_REQUEST: 'consultation_request',
  SCROLL_DEPTH: 'scroll_depth',
} as const;

export type StandardEventName = typeof standardEvents[keyof typeof standardEvents];
